import { FC, memo, useMemo } from 'react';
import { useController } from 'react-hook-form';

import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import { Skeleton } from '@material-ui/lab';

import { HelperTxt } from '../helperTxt/HelperTxt';
import { Select } from '../select/Select';
import { FormSelectObjectOption, FormSelectProps } from './FormItems.types';

const getDisabledValue = (option: FormSelectObjectOption | string | number | { id: string; value: string }) =>
  typeof option === 'object' && option !== null && 'disabled' in option ? !!option.disabled : false;

export const FormSelect: FC<FormSelectProps> = memo(
  ({
    control,
    name,
    size = 'medium',
    variant = 'outlined',
    options,
    required,
    isAlphaSortOptions,
    placeholder,
    loading = false,
    ...rest
  }) => {
    const {
      field,
      fieldState: { error },
    } = useController({
      name,
      control,
      defaultValue: '',
    });

    const helperText = useMemo(() => {
      if (error) {
        return error.message;
      }

      if (required) {
        return '*required';
      }

      return '';
    }, [error, required]);

    if (loading) {
      const skeletonHeight = size === 'small' ? 40 : size === 'large' ? 56 : 48;

      return (
        <FormControl fullWidth>
          <Skeleton variant="rect" height={skeletonHeight} />
          {required && <Skeleton variant="text" width={80} height={24} style={{ marginTop: 4 }} />}
        </FormControl>
      );
    }

    return (
      <FormControl error={!!error} fullWidth>
        <Select {...rest} {...field} required={required} size={size} variant={variant} error={!!error}>
          {placeholder && (
            <MenuItem value={0} disabled style={{ display: 'none' }}>
              <span className="select-placeholder">{placeholder}</span>
            </MenuItem>
          )}
          {Array.isArray(options)
            ? options.map((option, index) =>
                typeof option === 'object' ? (
                  <MenuItem key={index} value={option.value} disabled={getDisabledValue(option)}>
                    {option.name}
                  </MenuItem>
                ) : (
                  <MenuItem key={index} value={option}>
                    {option}
                  </MenuItem>
                ),
              )
            : isAlphaSortOptions
              ? Object.entries(options)
                  .map(entry => ({
                    id: entry[0],
                    value: String(entry[1]),
                  }))
                  .sort((a, b) => a.value.localeCompare(b.value))
                  .map((option, index) => (
                    <MenuItem key={index} value={option.id} disabled={getDisabledValue(option)}>
                      {option.value}
                    </MenuItem>
                  ))
              : Object.keys(options).map(key => (
                  <MenuItem key={key} value={key}>
                    {options[key]}
                  </MenuItem>
                ))}
        </Select>

        {helperText && <HelperTxt>{helperText}</HelperTxt>}
      </FormControl>
    );
  },
);
